import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  FC,
} from "react";
import { customersApi } from "@Api/customers";
import { KeycloakContext } from "@Contexts/KeycloakContext";
import { LoadingScreen } from "@Components/LoadingScreen";

interface CompanyContextType {
  companyId: number | null;
  companyName: string | null;
  companyDocument: string | null;
  setCompany: (id: number, name?: string, document?: string) => void;
  clearCompany: () => void;
  isVerifyingCompany: boolean;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error("useCompany must be used within CompanyProvider");
  }
  return context;
};

interface CompanyProviderProps {
  children: ReactNode;
}

const CURRENT_COMPANY_ID = "currentCompanyId";
const CURRENT_COMPANY_NAME = "currentCompanyName";
const CURRENT_COMPANY_DOCUMENT = "currentCompanyDocument";

export const CompanyProvider: FC<CompanyProviderProps> = ({ children }) => {
  const [companyId, setCompanyId] = useState<number | null>(() => {
    const storedId = localStorage.getItem(CURRENT_COMPANY_ID);
    return storedId ? parseInt(storedId) : null;
  });

  const [companyName, setCompanyName] = useState<string | null>(() => {
    return localStorage.getItem(CURRENT_COMPANY_NAME);
  });

  const [companyDocument, setCompanyDocument] = useState<string | null>(() => {
    return localStorage.getItem(CURRENT_COMPANY_DOCUMENT);
  });

  // Se há uma empresa armazenada, começamos verificando o acesso a ela antes de
  // liberar a aplicação (evita flash de conteúdo e enxurrada de 403).
  const [isVerifyingCompany, setIsVerifyingCompany] = useState<boolean>(
    () => !!localStorage.getItem(CURRENT_COMPANY_ID)
  );

  // Consumido de forma tolerante: em ambientes sem KeycloakProvider (ex.: testes)
  // simplesmente não há verificação de acesso a ser feita.
  const keycloak = useContext(KeycloakContext);
  const isAuthenticated = keycloak?.isAuthenticated ?? false;
  const isAuthLoading = keycloak?.isLoading ?? false;

  const setCompany = (id: number, name?: string, document?: string) => {
    setCompanyId(id);
    localStorage.setItem(CURRENT_COMPANY_ID, id.toString());

    if (name) {
      setCompanyName(name);
      localStorage.setItem(CURRENT_COMPANY_NAME, name);
    }

    if (document) {
      setCompanyDocument(document);
      localStorage.setItem(CURRENT_COMPANY_DOCUMENT, document);
    }

    // Recarrega a página para obter nova configuração
    window.location.reload();
  };

  const clearCompany = () => {
    setCompanyId(null);
    setCompanyName(null);
    setCompanyDocument(null);
    localStorage.removeItem(CURRENT_COMPANY_ID);
    localStorage.removeItem(CURRENT_COMPANY_NAME);
    localStorage.removeItem(CURRENT_COMPANY_DOCUMENT);
  };

  // Confirma, na inicialização, se o usuário ainda tem acesso à empresa
  // armazenada como última acessada. Caso o acesso tenha sido revogado, a
  // empresa é limpa.
  useEffect(() => {
    if (isAuthLoading) return;

    const storedId = localStorage.getItem(CURRENT_COMPANY_ID);

    if (!isAuthenticated || !storedId) {
      setIsVerifyingCompany(false);
      return;
    }

    let cancelled = false;
    setIsVerifyingCompany(true);

    customersApi
      .verifyCustomerAccess(parseInt(storedId))
      .then((hasAccess) => {
        if (cancelled) return;
        if (!hasAccess) {
          clearCompany();
        }
      })
      .catch(() => {
        // Erro transitório (rede/5xx): mantém a empresa atual.
      })
      .finally(() => {
        if (!cancelled) setIsVerifyingCompany(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isAuthLoading]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === CURRENT_COMPANY_ID) {
        const newId = e.newValue ? parseInt(e.newValue) : null;
        setCompanyId(newId);
      }
      if (e.key === CURRENT_COMPANY_NAME) {
        setCompanyName(e.newValue);
      }
      if (e.key === CURRENT_COMPANY_DOCUMENT) {
        setCompanyDocument(e.newValue);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  return (
    <CompanyContext.Provider
      value={{
        companyId,
        companyName,
        companyDocument,
        setCompany,
        clearCompany,
        isVerifyingCompany,
      }}
    >
      {isVerifyingCompany ? <LoadingScreen /> : children}
    </CompanyContext.Provider>
  );
};
